import React, { useMemo} from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { useProjectStore } from "../stores/project.store";
import { getStepRows } from "../utils/getStepRows";
import EnrichmentPlots from "./plots/EnrichmentPlots";
import TrajectoryPlots from "./plots/TrajectoryPlots"; // adjust path if needed
import BiomarkerDiscoveryResults from "./plots/BiomarkerDiscoveryResults";
import {
  QualityControlPlots,
  BarChart,
  ScatterPlot,
  NormalizationPlots,
  FeatureSelectionPlot,
  ClusterScatterPlot,
  UmapWithGeneSelection,
  DotPlot,
} from "./plots";
import { Alert, Box, Typography } from "@mui/material";

const Plot = createPlotlyComponent(Plotly);

const SINGLE_CELL_RESULT_STEPS = new Set([
  "quality control",
  "qc",
  "filter cells & genes",
  "doublet detection",
  "normalization",
  "feature selection",
  "dimensionality reduction",
  "umap",
  "clustering",
  "cell type annotation",
  "marker gene set",
  "de genes as markers",
  "de genes markers",
  "enrichment",
  "trajectory analysis",
]);



export interface ResultsPlotsProps {
  tableData: any[];
  plotData: any;
  dataType: string;
  projectName?: string;
  backendResult: any;
  // onResampleQc?: (nextSeed: number) => void;
  onResampleQc?: (args: { step: string; seed: number; artifactId: string; selectedGene?: string }) => void;
  onClusterRowsChange?: (rows: any[]) => void; 
  enrichmentSelectedCluster?: string | null;
  onEnrichmentSelectedClusterChange?: (id: string | null) => void;
}





const ResultsPlots = ({
  tableData,
  plotData,
  dataType,
  projectName,
  backendResult,
  onClusterRowsChange,
  enrichmentSelectedCluster,
  onEnrichmentSelectedClusterChange,
}: ResultsPlotsProps) => {
  const backendResult1 = useProjectStore((s) => s.backendResult);
  const stepResults = useProjectStore((s) => s.stepResults);
  // Prefer the result supplied by this Results view (important when opening a
  // saved run), while retaining live resampling fields from the store.
  // The previous store-first choice could leave the plot renderer on an older
  // step with no plotSample/tableData.
  const br = useMemo(
    () => ({ ...(backendResult1 ?? {}), ...(backendResult ?? {}) }),
    [backendResult1, backendResult]
  );
  const step = br?.currentStep;
  const normalizedStep = String(step ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace("filter cells genes", "filter cells & genes");
  const genesList = useMemo(() => {
    if (Array.isArray(br?.genesList) && br.genesList.length) return br.genesList;
    if (Array.isArray(backendResult1?.genesList) && backendResult1.genesList.length) return backendResult1.genesList;
    for (const result of Object.values(stepResults ?? {})) {
      if (Array.isArray((result as any)?.genesList) && (result as any).genesList.length) return (result as any).genesList;
    }
    return [];
  }, [br, backendResult1, stepResults]);
  // const selectedGene = br?.selectedGene;
  
 



 
  const rows = useMemo(() => getStepRows(br, tableData), [br, tableData]);
  const looksLikeQc = rows.some(
    (row: any) => row && ["n_genes_by_counts", "total_counts", "pct_counts_mt"]
      .every((key) => Object.prototype.hasOwnProperty.call(row, key))
  );

const normalizedDataType = String(dataType ?? "").trim().toLowerCase();
const isSingleCell =
  normalizedDataType === "single cell data" ||
  normalizedDataType === "single-cell data" ||
  normalizedDataType === "single_cell_data" ||
  normalizedDataType === "single_cell" ||
  SINGLE_CELL_RESULT_STEPS.has(normalizedStep) ||
  (looksLikeQc && !normalizedStep);


  const memoizedPlot = useMemo(() => {
  const wrapWithResampleBar = (_meta: any, child: React.ReactNode) => child;



    // --- QUALITY CONTROL ---
    if (isSingleCell && (
      step === "Quality Control" ||
      normalizedStep === "quality control" ||
      normalizedStep === "qc" ||
      // Only infer QC from columns when the result has no step label at all.
      // UMAP rows can legitimately contain QC metrics as metadata columns.
      (looksLikeQc && !normalizedStep)
    )) {
      const qcMeta = br?.qcMeta;
      const seed = qcMeta?.sampleSeed ?? br?.sampleSeed ?? 0;
      
      return wrapWithResampleBar({ ...br, ...(qcMeta || {}) }, <QualityControlPlots key={seed} data={rows} />);
    }

    // --- FEATURE SELECTION ---
    if (isSingleCell && normalizedStep === "feature selection") {
      if (!rows?.length) return <p>No feature selection data available.</p>;
      const fsMeta = br?.featureSelectionMeta ?? br?.qcMeta ?? {};
      return wrapWithResampleBar({ ...br, ...(fsMeta || {}) }, <FeatureSelectionPlot data={rows} />);
    }

    // --- FILTER CELLS & GENES ---
    const filterRows = getStepRows(br, tableData);
    if (isSingleCell && normalizedStep === "filter cells & genes" && filterRows.length > 0) {
      return <BarChart data={filterRows} title="Cell & Gene Filtering Summary" />;
    }

    // --- DOUBLET DETECTION ---
    if (isSingleCell && normalizedStep === "doublet detection") {
      return (
        <ScatterPlot
          data={tableData}
          xMetric="cell_index"
          yMetric="doublet_score"
          title="Doublet Detection Scores"
        />
      );
    }

    // --- UMAP ---
  if (isSingleCell && normalizedStep === "umap") {
  const selectedGene = br?.selectedGene ?? "AL627309.1";  // Default gene if none selected

  // If no UMAP data is available, show a message
  if (!rows?.length) return <p>No UMAP data available.</p>;

  // Simplified UMAP meta data handling (fallbacks included)
  const umapMeta =
    br?.umapMeta ??
    br ??
    {
      qcArtifactId: br?.qcArtifactId,
      artifactId: br?.artifactId,
      artifactType: br?.artifactType ?? "umap",
      sampleSeed: br?.sampleSeed,
      sampleN: br?.sampleN,
      totalCells: br?.totalCells,
    };

  const seed = umapMeta?.sampleSeed ?? br?.sampleSeed ?? 0;

  // Ensure UMAP data exists (either artifactId or qcArtifactId must be present)
  if (!umapMeta?.artifactId && !umapMeta?.qcArtifactId) {
    return <p>UMAP hasn’t been generated yet (missing UMAP artifact id).</p>;
  }



  // Proceed with UMAP component wrapping (passing all necessary data)
  return wrapWithResampleBar(
    umapMeta,
    <UmapWithGeneSelection
      key={`umap-${seed}`}
      backendResult={{
        ...br,
        selectedGene,
        // Use the latest umapData if available (has newest gene), fallback to rows
        tableData: br?.umapData ?? rows,
        umapData: br?.umapData ?? rows,
      }}
    />
  );
}



    // --- CLUSTERING ---
    if (isSingleCell && normalizedStep === "clustering") {
      const clusteringMeta = br?.clusteringMeta ?? br?.qcMeta ?? {};
      return wrapWithResampleBar(
        { ...br, ...(clusteringMeta || {}) },
        <ClusterScatterPlot data={rows} title="UMAP Clustering and Metadata" genesList={genesList} />
      );
    }

    if (isSingleCell && normalizedStep === "cell type annotation") {
      const annotationRows = getStepRows(
        { plotSample: br?.plotSample, tableData: br?.tableData },
        Array.isArray(tableData) ? tableData : [],
      );
      if (!annotationRows.length) return <p>No saved annotation UMAP data available.</p>;
      const clusterSummary = Array.isArray(br?.annotationMeta?.clusterSummary)
        ? br.annotationMeta.clusterSummary
        : [];
      const failedClusters = Array.isArray(br?.annotationMeta?.failedClusters)
        ? br.annotationMeta.failedClusters
        : [];
      return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          {failedClusters.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Annotation output could not be interpreted for {failedClusters.length} of {clusterSummary.length || "the"} clusters.
              Those cells are labeled “Annotation failed” instead of being reported as biologically unknown. Re-run annotation or review the cluster markers.
            </Alert>
          )}
          {br?.annotationMeta && (
            <Box sx={{ mb: 1, p: 1, border: "1px solid #9bb8d3", borderLeft: "4px solid #1976d2", borderRadius: 2, backgroundColor: "#f7fbff" }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Cell annotation · {br.annotationMeta.tissue} · {br.annotationMeta.markersPerCluster} markers per cluster
              </Typography>
              <Box component="details" sx={{ mt: 0.25, "& summary": { cursor: "pointer", color: "#1565c0", fontSize: 12, fontWeight: 700 } }}>
                <Box component="summary">View identities and confidence ({clusterSummary.length} clusters)</Box>
              {br.annotationMeta.foundUserMarkers?.length > 0 && (
                <Typography variant="body2" color="success.main">
                  User markers found: {br.annotationMeta.foundUserMarkers.join(", ")}
                </Typography>
              )}
              {br.annotationMeta.missingUserMarkers?.length > 0 && (
                <Typography variant="body2" color="warning.main">
                  User markers not found: {br.annotationMeta.missingUserMarkers.join(", ")}
                </Typography>
              )}
              {clusterSummary.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 150, overflow: "auto", border: "1px solid #dbe6ef", borderRadius: 1, backgroundColor: "white" }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 12, "& th, & td": { px: 1, py: 0.65, textAlign: "left", borderBottom: "1px solid #edf1f5" }, "& th": { position: "sticky", top: 0, backgroundColor: "#eef5fb", fontWeight: 700 } }}>
                    <thead><tr><th>Cluster</th><th>Assigned identity</th><th>Confidence</th><th>Cells</th></tr></thead>
                    <tbody>
                      {clusterSummary.map((row: any) => (
                        <tr key={String(row.cluster)}>
                          <td>{row.cluster}</td>
                          <td style={{ color: row.status === "failed" ? "#c62828" : "inherit" }}>{row.cellType}</td>
                          <td>{row.confidence}</td>
                          <td>{Number(row.cellCount ?? 0).toLocaleString()} ({Number(row.cellPercent ?? 0).toFixed(1)}%)</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              )}
              </Box>
            </Box>
          )}
          <ClusterScatterPlot
            key={`annotation-${br?.sampleSeed ?? 0}-${annotationRows.length}`}
            data={annotationRows}
            title="UMAP Cell Type Annotation"
            groupKey="annotation"
            genesList={genesList}
            className="w-full"
          />
        </Box>
      );
    }

    if (isSingleCell && normalizedStep === "marker gene set") {
      return <DotPlot data={rows} backendResult={br} />;
    }

    if (isSingleCell && (normalizedStep === "de genes as markers" || normalizedStep === "de genes markers")) {
      return <BiomarkerDiscoveryResults backendResult={br} legacyRows={rows} />;
    }

if (isSingleCell && normalizedStep === "trajectory analysis") {
  return <TrajectoryPlots backendResult={br} projectName={projectName} />;
}

if (isSingleCell && step?.toLowerCase() === "enrichment") {
  return (
    <EnrichmentPlots
      data={br?.tableData ?? tableData}
      selectedCluster={enrichmentSelectedCluster}
      onSelectedClusterChange={onEnrichmentSelectedClusterChange}
      onClusterRowsChange={onClusterRowsChange}
    />
  );
}

    if (isSingleCell && normalizedStep === "dimensionality reduction") {
      return (
        <ScatterPlot
          data={tableData}
          xMetric="Principal Component"
          yMetric="Variance Ratio"
          title="PCA Variance Ratio"
          defaultColor="#2c7bb6"
        />
      );
    }

    // --- NORMALIZATION (no bar unless you add it to RESAMPLE_STEPS) ---
   if (isSingleCell && normalizedStep === "normalization") {
  if (!rows?.length) return <p>No normalization data available.</p>;

  // Ensure the normalizationMeta is passed correctly (should contain artifactId, sampleSeed, etc.)
  const normMeta = br?.normalizationMeta ?? br?.qcMeta ?? {};  // Use normalizationMeta if it exists, otherwise fallback to qcMeta
  const seed = normMeta?.sampleSeed ?? br?.sampleSeed ?? 0;

  // Log the meta for debugging



  // Use wrapWithResampleBar to show the resample button for Normalization
  return wrapWithResampleBar({ ...br, ...(normMeta || {}) }, <NormalizationPlots key={seed} data={rows} />);
}


    // --- fallback plotly ---
    if (plotData) {
      return (
        <Plot
          data={plotData.data}
          layout={plotData.layout}
          config={{ ...plotData.config }}
          style={{ width: "100%", height: "100%" }}
        />
      );
    }

    return <p>No plot data available.</p>;
  }, [
    isSingleCell,
    step,
    normalizedStep,
    looksLikeQc,
    br,
    rows,
    tableData,
    plotData,
    genesList,
    onClusterRowsChange,
    enrichmentSelectedCluster,            // ✅ add
    onEnrichmentSelectedClusterChange,
      projectName,
  ]);

  return memoizedPlot;
};

export default ResultsPlots;
